import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Row,
  Col,
  Form,
  Pagination,
  Spinner,
  Table,
  Card,
} from 'react-bootstrap';
import Select from 'react-select';
import { FaFilter, FaTimes, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { salvarEmExcel } from '~/Utils/Helpers';
import {
  listarGruposEmpresaAnaliseEmpreendimento,
  consultarAnaliseEmpreendimento,
} from '~/Services/Service';

const RECORDS_PER_PAGE = 15;

// 1o dia do mês atual (RN-18 — Inicializacao_Data_Padrao, URelAnaliseEmp.pas:536)
const primeiroDiaMes = () => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  return `${ano}-${mes}-01`;
};

// hoje no formato YYYY-MM-DD
const hojeISO = () => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  const dia = String(hoje.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

const filtroInicial = () => ({
  dataInicio: primeiroDiaMes(),
  dataFim: hojeISO(),
  porDataVenda: true, // rvenda default (RN-3)
  ges: [],
  codigoGrupoEmpresaEspecifico: null,
  soComEstoque: false,
  soComVenda: false,
  soComContratoAssinado: false,
});

const formatarMoeda = valor => {
  const num = Number(valor) || 0;
  return num.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const RelatorioAnaliseEmpreendimento = () => {
  const [grupos, setGrupos] = useState([]);
  const [filtros, setFiltros] = useState(filtroInicial());
  const [resultados, setResultados] = useState([]);
  const [executou, setExecutou] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);

  useEffect(() => {
    let ativo = true;
    listarGruposEmpresaAnaliseEmpreendimento()
      .then(res => {
        if (ativo) setGrupos(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (ativo) toast.error('Erro ao carregar Grupos de Empresa.');
      });
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesGe = useMemo(
    () =>
      (grupos || []).map(g => ({
        value: g.codigo_grupo_empresa,
        label: `${g.codigo_grupo_empresa} - ${g.apelido || ''}`,
      })),
    [grupos]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(resultados.length / RECORDS_PER_PAGE)
  );

  const dadosPaginados = useMemo(() => {
    const last = paginaAtual * RECORDS_PER_PAGE;
    const first = last - RECORDS_PER_PAGE;
    return resultados.slice(first, last);
  }, [resultados, paginaAtual]);

  const montarPayload = () => ({
    data_inicio: filtros.dataInicio || null,
    data_fim: filtros.dataFim || null,
    por_data_venda: filtros.porDataVenda,
    ges: filtros.ges.map(g => g.value),
    codigo_grupo_empresa_especifico:
      filtros.codigoGrupoEmpresaEspecifico &&
      filtros.codigoGrupoEmpresaEspecifico.value
        ? filtros.codigoGrupoEmpresaEspecifico.value
        : null,
    so_com_estoque: filtros.soComEstoque,
    so_com_venda: filtros.soComVenda,
    so_com_contrato_assinado: filtros.soComContratoAssinado,
  });

  // Validação inline (RN-1 período / RN-2 GE) — espelha o backend
  const validar = () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      toast.warning('Período inválido. Informe data inicial e final.');
      return false;
    }
    if (filtros.dataInicio > filtros.dataFim) {
      toast.warning('Data inicial deve ser menor ou igual à data final.');
      return false;
    }
    if (!filtros.ges || filtros.ges.length === 0) {
      toast.warning('Nenhum GE escolhido!');
      return false;
    }
    return true;
  };

  const handleFiltrar = async e => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validar()) return;
    setLoading(true);
    try {
      const res = await consultarAnaliseEmpreendimento(montarPayload());
      const lista = Array.isArray(res) ? res : [];
      setResultados(lista);
      setExecutou(true);
      setPaginaAtual(1);
      toast.success(`${lista.length} empreendimento(s) encontrado(s).`);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gerar relatório.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLimpar = () => {
    setFiltros(filtroInicial());
    setResultados([]);
    setExecutou(false);
    setPaginaAtual(1);
  };

  const handleExportar = async () => {
    if (!validar()) return;
    setExportando(true);
    try {
      await salvarEmExcel(montarPayload(), '/analise-empreendimento/exportar-excel');
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar Excel.');
    } finally {
      setExportando(false);
    }
  };

  const renderPaginacao = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const maxVisible = 5;
    const start = Math.max(1, paginaAtual - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    items.push(
      <Pagination.First
        key="first"
        onClick={() => setPaginaAtual(1)}
        disabled={paginaAtual === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPaginaAtual(paginaAtual - 1)}
        disabled={paginaAtual === 1}
      />
    );
    for (let p = start; p <= end; p += 1) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === paginaAtual}
          onClick={() => setPaginaAtual(p)}
        >
          {p}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setPaginaAtual(paginaAtual + 1)}
        disabled={paginaAtual === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => setPaginaAtual(totalPages)}
        disabled={paginaAtual === totalPages}
      />
    );
    return (
      <div className="d-flex justify-content-end">
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <div>
      <Header title="Relatório de Análise de Empreendimento" />

      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={handleFiltrar}>
            <Row>
              <Col xs={12} sm={6} md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Data Início</Form.Label>
                  <Form.Control
                    type="date"
                    value={filtros.dataInicio}
                    onChange={ev =>
                      setFiltros({ ...filtros, dataInicio: ev.target.value })
                    }
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Data Fim</Form.Label>
                  <Form.Control
                    type="date"
                    value={filtros.dataFim}
                    onChange={ev =>
                      setFiltros({ ...filtros, dataFim: ev.target.value })
                    }
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="small mb-1">Critério de período</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Data venda (efetivação)"
                    name="criterioPeriodo"
                    checked={filtros.porDataVenda === true}
                    onChange={() =>
                      setFiltros({ ...filtros, porDataVenda: true })
                    }
                    disabled={loading}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Data proposta"
                    name="criterioPeriodo"
                    checked={filtros.porDataVenda === false}
                    onChange={() =>
                      setFiltros({ ...filtros, porDataVenda: false })
                    }
                    disabled={loading}
                  />
                </div>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12} md={8}>
                <Form.Group>
                  <Form.Label className="small mb-1">
                    Grupos de Empresa (obrigatório)
                  </Form.Label>
                  <Select
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={opcoesGe}
                    value={filtros.ges}
                    onChange={opts =>
                      setFiltros({ ...filtros, ges: opts || [] })
                    }
                    isMulti
                    isDisabled={loading}
                    placeholder="Selecione um ou mais GEs"
                    noOptionsMessage={() => 'Nenhum GE disponível'}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="small mb-1">
                    Empreendimento específico (opcional)
                  </Form.Label>
                  <Select
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    options={opcoesGe}
                    value={filtros.codigoGrupoEmpresaEspecifico}
                    onChange={opt =>
                      setFiltros({
                        ...filtros,
                        codigoGrupoEmpresaEspecifico: opt || null,
                      })
                    }
                    isClearable
                    isDisabled={loading}
                    placeholder="Todos"
                    noOptionsMessage={() => 'Nenhum GE disponível'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col xs={12}>
                <Form.Label className="small mb-1">Filtros de resultado</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="checkbox"
                    label="Só com estoque disponível"
                    checked={filtros.soComEstoque}
                    onChange={ev =>
                      setFiltros({ ...filtros, soComEstoque: ev.target.checked })
                    }
                    disabled={loading}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    label="Só com venda no período"
                    checked={filtros.soComVenda}
                    onChange={ev =>
                      setFiltros({ ...filtros, soComVenda: ev.target.checked })
                    }
                    disabled={loading}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    label="Só com contrato assinado"
                    checked={filtros.soComContratoAssinado}
                    onChange={ev =>
                      setFiltros({
                        ...filtros,
                        soComContratoAssinado: ev.target.checked,
                      })
                    }
                    disabled={loading}
                  />
                </div>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col className="d-flex justify-content-end">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleLimpar}
                  disabled={loading}
                  className="mr-2"
                >
                  <FaTimes className="mr-1" /> Limpar
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <FaFilter className="mr-1" /> Gerar
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {executou && resultados.length === 0 && !loading && (
        <Card>
          <Card.Body className="text-center py-4 text-muted">
            Nenhum registro encontrado!
          </Card.Body>
        </Card>
      )}

      {resultados.length > 0 && (
        <Card className="mb-3">
          <Card.Header className="d-flex align-items-center">
            <small className="text-muted">
              {resultados.length} empreendimento(s) — exibindo{' '}
              {dadosPaginados.length}
            </small>
            <Button
              variant="outline-success"
              onClick={handleExportar}
              disabled={exportando || loading}
              className="ml-auto"
            >
              {exportando ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-1" />
                  Exportando...
                </>
              ) : (
                <>
                  <FaFileExcel className="mr-1" /> Exportar Excel
                </>
              )}
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table striped bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th>Código GE</th>
                    <th>Empreendimento</th>
                    <th className="text-right">Vl Vendas</th>
                    <th className="text-right">Qt Vendas</th>
                    <th className="text-right">Vl Vista</th>
                    <th className="text-right">Qt Pré-venda</th>
                    <th className="text-right">Qt Rescindidos</th>
                    <th className="text-right">Qt Pré-vendas Canc.</th>
                    <th className="text-right">Qt Disponíveis</th>
                    <th className="text-right">Qt Contr. Assinados</th>
                    <th className="text-right">Qt Contr. Enviados</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosPaginados.map((item, idx) => (
                    <tr
                      key={`ae-${item.codigo_grupo_empresa}-${item.codigo_familia}-${idx}`}
                    >
                      <td>{item.codigo_grupo_empresa}</td>
                      <td>{item.nome_familia}</td>
                      <td className="text-right">{formatarMoeda(item.vendas)}</td>
                      <td className="text-right">{item.qtd_vendas}</td>
                      <td className="text-right">{formatarMoeda(item.vista)}</td>
                      <td className="text-right">{item.pre_venda}</td>
                      <td className="text-right">{item.rescindidos}</td>
                      <td className="text-right">{item.pre_vendas_canceladas}</td>
                      <td className="text-right">{item.disponiveis}</td>
                      <td className="text-right">{item.assinados}</td>
                      <td className="text-right">{item.enviados}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          {totalPages > 1 && <Card.Footer>{renderPaginacao()}</Card.Footer>}
        </Card>
      )}
    </div>
  );
};

export default RelatorioAnaliseEmpreendimento;
